export type JwtDecodeErrorCode = 'invalid-format' | 'invalid-base64url' | 'invalid-json'

export interface DecodedJwt {
  header: Record<string, unknown>
  payload: Record<string, unknown>
}

export class JwtDecodeError extends Error {
  constructor(
    public readonly code: JwtDecodeErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'JwtDecodeError'
  }
}

function decodeBase64Url(segment: string): Uint8Array {
  if (!segment || !/^[A-Za-z0-9_-]+$/.test(segment) || segment.length % 4 === 1) {
    throw new JwtDecodeError('invalid-base64url', 'Invalid Base64URL segment')
  }

  const standard = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4)

  try {
    const binary = atob(padded)
    return Uint8Array.from(binary, (character) => character.charCodeAt(0))
  } catch {
    throw new JwtDecodeError('invalid-base64url', 'Invalid Base64URL segment')
  }
}

function parseJsonObject(segment: string): Record<string, unknown> {
  const bytes = decodeBase64Url(segment)

  let json: string
  try {
    json = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    throw new JwtDecodeError('invalid-json', 'JWT segment is not valid UTF-8 JSON')
  }

  try {
    const value: unknown = JSON.parse(json)
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('JWT segment must contain a JSON object')
    }
    return value as Record<string, unknown>
  } catch {
    throw new JwtDecodeError('invalid-json', 'JWT segment does not contain valid JSON')
  }
}

function validateSignature(signature: string, header: Record<string, unknown>): void {
  const isUnsecuredJwt = header.alg === 'none'

  if (isUnsecuredJwt) {
    if (signature !== '') {
      throw new JwtDecodeError(
        'invalid-format',
        'JWT with alg none must have an empty signature segment',
      )
    }
    return
  }

  if (signature === '') {
    throw new JwtDecodeError('invalid-format', 'Signed JWT must have a signature')
  }

  decodeBase64Url(signature)
}

export function decodeJwt(token: string): DecodedJwt {
  const parts = token.trim().split('.')
  const [headerSegment, payloadSegment, signatureSegment] = parts
  if (
    parts.length !== 3 ||
    !headerSegment ||
    !payloadSegment ||
    signatureSegment === undefined
  ) {
    throw new JwtDecodeError('invalid-format', 'JWT must contain three dot-separated segments')
  }

  const header = parseJsonObject(headerSegment)
  const payload = parseJsonObject(payloadSegment)
  validateSignature(signatureSegment, header)

  return { header, payload }
}
